import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { seoAPI, clientsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import { handleApiError, showToast, getImageUrl } from '../../utils/helpers';
import { X } from 'lucide-react';
import MediaLibraryPickerModal from '../media/MediaLibraryPickerModal';

const SeoKeywordEditModal = ({ keyword, clientId, clientName, isOpen, onClose, onSaved }) => {
  const { isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [clientPosts, setClientPosts] = useState([]);
  const [form, setForm] = useState({
    keyword: '',
    meta_title: '',
    meta_description: '',
    backlink_url: '',
    publish: true,
    status: 'draft',
    article_post_id: '',
    design_media_id_1: '',
    design_media_id_2: '',
    design_media_1: null,
    design_media_2: null,
  });
  const [mediaPickerField, setMediaPickerField] = useState(null);

  useEffect(() => {
    if (isOpen && clientId) {
      fetchClientPosts();
    }
  }, [isOpen, clientId]);

  useEffect(() => {
    if (keyword) {
      setForm({
        keyword: keyword.keyword || '',
        meta_title: keyword.meta_title || '',
        meta_description: keyword.meta_description || '',
        backlink_url: keyword.backlink_url || '',
        publish: keyword.publish !== false,
        status: keyword.status || 'draft',
        article_post_id: keyword.article_post_id || '',
        design_media_id_1: keyword.design_media_id_1 || '',
        design_media_id_2: keyword.design_media_id_2 || '',
        design_media_1: keyword.design_media_1 || null,
        design_media_2: keyword.design_media_2 || null,
      });
    }
  }, [keyword]);

  const fetchClientPosts = async () => {
    try {
      const res = await clientsAPI.getPosts(clientId);
      setClientPosts(res.data.posts || []);
    } catch (err) {
      console.error('Failed to fetch client posts:', err);
    }
  };

  const articles = clientPosts.filter((p) => p.content_type === 'blog');
  const designs = clientPosts.filter((p) => p.content_type && p.content_type !== 'blog');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.keyword.trim()) {
      showToast.error(isRTL ? 'الكلمة المفتاحية مطلوبة' : 'Keyword is required');
      return;
    }
    try {
      setLoading(true);
      await seoAPI.updateKeyword(keyword.id, {
        keyword: form.keyword.trim(),
        meta_title: form.meta_title.trim() || null,
        meta_description: form.meta_description.trim() || null,
        backlink_url: form.backlink_url.trim() || null,
        publish: form.publish,
        status: form.status,
        article_post_id: form.article_post_id || null,
        design_media_id_1: form.design_media_id_1 || null,
        design_media_id_2: form.design_media_id_2 || null,
      });
      showToast.success(isRTL ? 'تم تحديث الكلمة' : 'Keyword updated');
      onSaved?.();
      onClose();
    } catch (err) {
      showToast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir={isRTL ? 'rtl' : 'ltr'}>
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isRTL ? 'تعديل الكلمة المفتاحية' : 'Edit keyword'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'الكلمة المفتاحية' : 'Keyword'} *
            </label>
            <input
              type="text"
              value={form.keyword}
              onChange={(e) => setForm((f) => ({ ...f, keyword: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'عنوان الميتا' : 'Meta title'}
            </label>
            <input
              type="text"
              value={form.meta_title}
              onChange={(e) => setForm((f) => ({ ...f, meta_title: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'وصف الميتا' : 'Meta description'}
            </label>
            <textarea
              value={form.meta_description}
              onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))}
              rows={2}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'رابط النشر (Backlink)' : 'Backlink URL'}
            </label>
            <input
              type="url"
              value={form.backlink_url}
              onChange={(e) => setForm((f) => ({ ...f, backlink_url: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              placeholder="https://"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              {isRTL ? 'الحالة' : 'Status'}
            </label>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="draft">{isRTL ? 'مسودة' : 'Draft'}</option>
              <option value="proposed">{isRTL ? 'مُرسلة للعميل' : 'Proposed (sent to client)'}</option>
              <option value="approved">{isRTL ? 'معتمدة' : 'Approved'}</option>
              <option value="rejected">{isRTL ? 'مرفوضة' : 'Rejected'}</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="edit-publish"
              type="checkbox"
              checked={form.publish}
              onChange={(e) => setForm((f) => ({ ...f, publish: e.target.checked }))}
              className="h-4 w-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500"
            />
            <label htmlFor="edit-publish" className="text-sm text-slate-700">
              {isRTL ? 'جاهزة للنشر' : 'Ready to publish'}
            </label>
          </div>

          <div className="border-t border-slate-200 pt-4 space-y-4">
            <h4 className="text-sm font-medium text-slate-700">
              {isRTL ? 'ربط بالمحتوى والتصاميم' : 'Link to content & designs'}
            </h4>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'المحتوى (مقال)' : 'Content (article)'}
              </label>
              <select
                value={form.article_post_id}
                onChange={(e) => setForm((f) => ({ ...f, article_post_id: e.target.value }))}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <option value="">{isRTL ? '-- لا يوجد --' : '-- None --'}</option>
                {articles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} {p.serial_number ? `#${p.serial_number}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'التصميم 1' : 'Design 1'}
              </label>
              <div className="flex items-center gap-2">
                {form.design_media_1 ? (
                  <>
                    <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 shrink-0">
                      <img src={getImageUrl(form.design_media_1.file_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm text-slate-700 truncate flex-1">{form.design_media_1.file_name}</span>
                  </>
                ) : (
                  <span className="text-slate-400 text-sm">{isRTL ? 'لم يتم الاختيار' : 'Not selected'}</span>
                )}
                <Button type="button" variant="secondary" size="sm" onClick={() => setMediaPickerField('design_media_id_1')}>
                  {form.design_media_1 ? (isRTL ? 'تغيير' : 'Change') : (isRTL ? 'اختر من المكتبة' : 'Select from library')}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {isRTL ? 'التصميم 2' : 'Design 2'}
              </label>
              <div className="flex items-center gap-2">
                {form.design_media_2 ? (
                  <>
                    <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 shrink-0">
                      <img src={getImageUrl(form.design_media_2.file_url)} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="text-sm text-slate-700 truncate flex-1">{form.design_media_2.file_name}</span>
                  </>
                ) : (
                  <span className="text-slate-400 text-sm">{isRTL ? 'لم يتم الاختيار' : 'Not selected'}</span>
                )}
                <Button type="button" variant="secondary" size="sm" onClick={() => setMediaPickerField('design_media_id_2')}>
                  {form.design_media_2 ? (isRTL ? 'تغيير' : 'Change') : (isRTL ? 'اختر من المكتبة' : 'Select from library')}
                </Button>
              </div>
            </div>
          </div>

          <div className="flex gap-2 justify-end pt-4 border-t border-slate-200">
            <Button type="button" variant="secondary" onClick={onClose}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button type="submit" loading={loading}>
              {isRTL ? 'حفظ' : 'Save'}
            </Button>
          </div>
        </form>
      </Card>
      {mediaPickerField && (
        <MediaLibraryPickerModal
          clientId={clientId}
          clientName={clientName}
          isOpen
          onClose={() => setMediaPickerField(null)}
          onSelect={(media) => {
            if (media) {
              setForm((f) => ({
                ...f,
                [mediaPickerField]: media.id,
                [mediaPickerField.replace('_id_', '_')]: media,
              }));
            } else {
              const base = mediaPickerField.replace('_id_', '_');
              setForm((f) => ({
                ...f,
                [mediaPickerField]: '',
                [base]: null,
              }));
            }
            setMediaPickerField(null);
          }}
          imageOnly
        />
      )}
    </div>
  );
};

export default SeoKeywordEditModal;
